import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { mapData, moduleHealth, tableActivity } from '../lib/brainHealth'

// Admin > Brain map. The map page comes from /api/admin?resource=brain_map (platform admin + two-factor, checked on the
// server) and runs in a sandboxed frame with no access to this app or its login. The live health numbers are loaded here
// and handed to the frame; the frame itself never calls the API.
async function adminGet(resource) {
  const { data } = await supabase.auth.getSession()
  const r = await fetch('/api/admin?resource=' + resource, { headers: { Authorization: 'Bearer ' + (data.session && data.session.access_token) } })
  if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || 'HTTP ' + r.status) }
  return r
}

// "Open in new tab": a blank tab (about:blank - no address anyone else can open) holding the same sandboxed frame, filled
// from this page with the map it already loaded through the admin check, plus the health numbers. Reloading that tab
// empties it: open it again from here.
export function tabPage(html) {
  const esc = s => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;')
  return '<!doctype html><html><head><meta charset="utf-8"><title>Hey Captain brain map</title><meta name="viewport" content="width=device-width, initial-scale=1">'
    + '<style>html,body{margin:0;height:100%;background:#0A1628}iframe{border:0;width:100%;height:100%;display:block}</style></head><body>'
    + '<iframe id="f" title="Brain map" sandbox="allow-scripts" srcdoc="' + esc(html) + '"></iframe></body></html>'
}

export default function BrainMapAdmin() {
  const [html, setHtml] = useState(''), [err, setErr] = useState(''), [health, setHealth] = useState(''), [busy, setBusy] = useState(false)
  const [full, setFull] = useState(false), [height, setHeight] = useState(600)   // full: false | 'native' | 'css'
  const frame = useRef(null), box = useRef(null), last = useRef(null), lastSec = useRef(null)
  useEffect(() => { adminGet('brain_map').then(r => r.text()).then(setHtml).catch(e => setErr(e.message)) }, [])

  // the map uses all the width and the height down to the bottom of the window
  useEffect(() => {
    const fitBox = () => { if (box.current) setHeight(Math.max(420, Math.floor(window.innerHeight - box.current.getBoundingClientRect().top - 16))) }
    fitBox(); window.addEventListener('resize', fitBox)
    return () => window.removeEventListener('resize', fitBox)
  }, [html])

  // full screen: the browser's own (Esc leaves it); where that is refused, the box covers the window (Esc from the frame)
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement) setFull(f => (f === 'native' ? false : f)) }
    const onMsg = ev => { if (frame.current && ev.source === frame.current.contentWindow && ev.data && ev.data.type === 'hc-map-esc') exitFull() }
    const onKey = ev => { if (ev.key === 'Escape') exitFull() }
    document.addEventListener('fullscreenchange', onFs); window.addEventListener('message', onMsg); window.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('fullscreenchange', onFs); window.removeEventListener('message', onMsg); window.removeEventListener('keydown', onKey) }
  }, [])
  async function enterFull() {
    try { await box.current.requestFullscreen(); setFull('native') } catch (e) { setFull('css') }
  }
  function exitFull() {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {})
    setFull(false)
  }

  async function loadHealth() {
    const G = mapData(html); if (!G || !frame.current) return
    setBusy(true); setHealth('')
    try {
      const j = await (await adminGet('brain_health')).json()
      const msg = { type: 'hc-health', modules: moduleHealth(G, j), activity: tableActivity(j), at: j.at, note: j.tables_error || '' }
      last.current = msg
      frame.current.contentWindow.postMessage(msg, '*')
      const bad = Object.values(msg.modules).filter(m => m.errors).length
      setHealth((bad ? bad + ' module(s) with errors' : 'No errors') + ' in 7 days' + (j.tables_error ? ' · last write times missing: run sql/brain-map-health.sql' : ''))
    } catch (e) { setHealth('Health not loaded: ' + e.message) } finally { setBusy(false) }
    loadSecurity()
  }
  // the live security check (red badges in the map): the same checks as the deploy gate, on the live database
  async function loadSecurity() {
    if (!frame.current) return
    let msg
    try { msg = { type: 'hc-security', ...(await (await adminGet('brain_security')).json()) } } catch (e) { msg = { type: 'hc-security', error: e.message } }
    lastSec.current = msg
    if (frame.current) frame.current.contentWindow.postMessage(msg, '*')
  }
  function openTab() {
    const w = window.open('', '_blank')
    if (!w) { setHealth('The browser blocked the new tab - allow pop-ups for this site'); return }
    w.document.open(); w.document.write(tabPage(html)); w.document.close()
    const f = w.document.getElementById('f')
    f.addEventListener('load', () => { for (const m of [last.current, lastSec.current]) if (m) f.contentWindow.postMessage(m, '*') })
  }

  if (err) return <div style={{ padding: 20, color: 'var(--danger, #e06470)' }}>Brain map: {err}</div>
  if (!html) return <div style={{ padding: 20, color: 'var(--muted)' }}>Loading brain map&hellip;</div>
  const btn = { padding: '6px 10px', fontSize: 12.5, background: 'rgba(10,22,40,.88)', color: '#E7EDF6', border: '1px solid #22365a', borderRadius: 8, cursor: 'pointer' }
  const boxStyle = full === 'css' ? { position: 'fixed', inset: 0, zIndex: 1000, background: '#0A1628' }
    : full === 'native' ? { position: 'relative', width: '100%', height: '100%', background: '#0A1628' }
    : { position: 'relative', width: '100%', height, border: '0.5px solid var(--border)', borderRadius: 12, overflow: 'hidden', background: '#0A1628' }
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <button className="btn" onClick={loadHealth} disabled={busy}>{busy ? 'Loading…' : 'Refresh health'}</button>
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>{health || 'Live health: last write per module and errors of the last 7 days.'} Security: the live check runs with it (red rings in the map).</span>
      </div>
      <div ref={box} style={boxStyle}>
        <iframe ref={frame} title="Brain map" sandbox="allow-scripts" srcDoc={html} onLoad={loadHealth}
          style={{ width: '100%', height: '100%', border: 0, display: 'block' }} />
        <div style={{ position: 'absolute', top: 10, right: 12, display: 'flex', gap: 6, zIndex: 8 }}>
          {!full && <button style={btn} onClick={openTab} title="Open the map in a new tab of this browser">Open in new tab</button>}
          <button style={btn} onClick={full ? exitFull : enterFull} title={full ? 'Leave full screen (Esc)' : 'Full screen (Esc to leave)'}>{full ? '✕ Exit full screen' : '⛶ Full screen'}</button>
        </div>
      </div>
    </div>
  )
}
